import type dayjs from "dayjs"

import type { ProjectDocument } from "../../Project/types/project-documents.types.ts"
import type { FilesFilters } from "../types/files-filters.types.ts"
import { classifyFileFormat } from "./classify-file-format.ts"
import { matchesDateRange } from "./date-range-filter.ts"

interface DocumentFilterContext {
  now?: dayjs.Dayjs
  /** The folder plus, when "include subfolders" is on, every descendant. */
  folderIds?: Set<string> | null
}

// The document endpoint answers none of the drawer filters, so they are all
// applied here client-side, over whichever page of documents is loaded.
export const documentMatchesFilters = (
  document: ProjectDocument,
  filters: FilesFilters,
  { now, folderIds }: DocumentFilterContext = {}
): boolean => {
  // Attachment's own generic-FK "attached to" concept — a document has none.
  if (filters.model_type) return false
  // A document can never belong to an album — that relationship only exists
  // on the Attachment side of the model.
  if (filters.album) return false

  if (filters.file_type) {
    const category = classifyFileFormat({ name: document.file_name })
    if (category !== filters.file_type) return false
  }

  if (filters.folder) {
    const ids = folderIds ?? new Set([filters.folder])
    if (!document.folder || !ids.has(document.folder)) return false
  }

  if (filters.owner && document.uploaded_by !== filters.owner) return false

  if (filters.document_type && document.document_type !== filters.document_type) {
    return false
  }

  if (filters.status && document.status !== filters.status) {
    return false
  }

  if (filters.related_record_type && filters.related_record_id) {
    const related = document.related_record
    if (
      related?.type !== filters.related_record_type ||
      related?.id !== filters.related_record_id
    ) {
      return false
    }
  }

  if (!matchesDateRange(document.created, filters, now)) return false

  return true
}
